import { cloneDeep } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { APPS } from "../../../../../../constants/AppsConstants";
import { ImageEditingFeatureDetector } from "../../../../../../constants/ImageEditingFeatures";
import { engineExpansionError } from "../../../../../../constants/ImageToImageConstants";
import RouterConstants from "../../../../../../constants/RouterConstants";
import { IMAGE_EDITING_FEATURES } from "../../../../../../constants/routesMapper";
import { checkImageHasTransparentPixels } from "../../../../../../helpers/images";
import { IRootStore } from "../../../../../../mobx/root-store";
import { ImageToImageConfigType, PlaygroundImage, PlaygroundResult } from "../../../../../../models/image-to-image";
import { IframePostMessageActionTypes } from "../../../../../../models/new-iframe";
import ImagesService from "../../../../../../services/ImagesService";
import QueryService from "../../../../../../utils/QueryService";
import { getCroppedImg } from "../../../../../../utils/cropImage";
import iframeStore from "../../../../../IframeNew/iframe-store";
import { IImageToImageStore } from "../../../image-to-image-store";

export interface IManualPlacementStore {
	isResizingImages: boolean;
	isManualPlacementConfigEnabled: boolean;
	expandBackground: (
		image: PlaygroundImage,
		config?: ImageToImageConfigType,
		keep_aspect_ratio_value_in_config?: boolean,
	) => Promise<void>;
	cropImage: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	increaseImageResolution: (
		image: PlaygroundImage,
		resolution: number,
		config?: ImageToImageConfigType,
	) => Promise<void>;
	toggleManualPlacementConfig: (status: boolean) => void;
}

export class ManualPlacementStore implements IManualPlacementStore {
	private queryService: QueryService = new QueryService("/image-to-image");
	private resizingAbortController: AbortController = new AbortController();
	rootStore: IRootStore;
	imageToImageStore: IImageToImageStore;
	isResizingImages: boolean = false;
	isManualPlacementConfigEnabled: boolean = false;

	constructor(rootStore: IRootStore, imageToImageStore: IImageToImageStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.imageToImageStore = imageToImageStore;
	}

	private isCanvasSizeDiffers = (
		originalImageElement: { width: number; height: number },
		customCropSize: { width: number | undefined; height: number | undefined },
		canvasSize?: { width: number; height: number },
	): boolean => {
		let canvasSizeDimensionsDiffers: boolean = false;
		let customSizeDimensionsDiffers: boolean = false;

		if (canvasSize)
			canvasSizeDimensionsDiffers =
				canvasSize.width !== originalImageElement.width || canvasSize.height !== originalImageElement.height;

		if (customCropSize?.width && customCropSize?.height)
			customSizeDimensionsDiffers =
				customCropSize.width !== originalImageElement.width ||
				customCropSize.height !== originalImageElement.height;

		return canvasSizeDimensionsDiffers || customSizeDimensionsDiffers;
	};

	expandBackground = async (
		image: PlaygroundImage,
		config: ImageToImageConfigType = this.imageToImageStore.config,
		keep_aspect_ratio_value_in_config = false,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const savedIndex = playgroundStore.playgroundResults.length;

		runInAction(() => {
			this.isResizingImages = true;
		});

		this.imageToImageStore.editingCanvasStore.showLoadingSkeletons(config, 1, true, playgroundStore.selectedConfig);
		try {
			const formData = new FormData();

			formData.append(
				"config",
				JSON.stringify({
					...config,
					original_image: {
						...image,
						offline_props: {
							...image.offline_props,
						},
					},
				}),
			);
			const result = await this.queryService.post(
				"/expand",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.resizingAbortController.signal },
			);

			if (!result?.image_res) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			} else if (result) {
				const { image_res } = result;
				const { playgroundResults } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: image_res,
					loading: true,
					type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
						? APPS.AUTOMOTIVE
						: APPS.IMAGE_TO_IMAGE,
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
					} as ImageToImageConfigType,

					image_config: {
						background: {
							expand: true,
						},
					} as ImageToImageConfigType,
					mode: "single",
				};
				if (!keep_aspect_ratio_value_in_config) {
					newImage.config.size = {};
				}

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}

				iframeStore.sendImageEditingPostMessage({
					action: IframePostMessageActionTypes.Generate,
					feature:
						ImageEditingFeatureDetector.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND],
					inputImage: image,
					resultUrls: [image_res],
					config: { ...config, background: { ...config.background, expand: true } },
				});

				// Increment global generation count (for engagement popup logic)
				this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
			} else {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_: PlaygroundResult, index: number) => index !== savedIndex,
					);
					throw new Error(`Error erasing object`);
				});
			}
		} catch (e: any) {
			if (e?.message.includes("422") && e.response.data.message !== engineExpansionError) {
				runInAction(() => {
					this.imageToImageStore.thumbnailNavigationSlideToImage = image;
				});
			} else if (e?.response?.data?.message === engineExpansionError) {
				this.imageToImageStore.thumbnailNavigationSlideToImage = image;
			}

			this.imageToImageStore.showAPIError(e);
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_: PlaygroundResult, index: number) => index !== savedIndex,
				);
				// Properly reselect the original image after removing failed skeleton
				playgroundStore.handleSelectSingleImage(image);
				throw new Error(`Error erasing object: ${e.message || e.toString()}`);
			});
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	cropImage = async (
		image: PlaygroundImage,
		config: ImageToImageConfigType = cloneDeep(this.imageToImageStore.config),
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const savedIndex = playgroundStore.playgroundResults.length;
		const croppedAreaPixels = config.manual_placement?.cropped_area_pixels;

		if (!croppedAreaPixels) {
			return;
		}

		runInAction(() => {
			this.isResizingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		try {
			// Get canvas size for manual placement crop operations
			const canvasSize = config.manual_placement?.canvas_size
				? { width: config.manual_placement.canvas_size[0], height: config.manual_placement.canvas_size[1] }
				: undefined;
			const customCropSize = { width: config.manual_placement?.width, height: config.manual_placement?.height };
			const croppedImageUrl = await getCroppedImg(image.url, croppedAreaPixels, canvasSize);

			// Check if canvas size differs from original image size
			// If so, use the cropped image directly without calling /crop API
			const originalImageElement = new Image();
			originalImageElement.src = image.url;
			await new Promise((resolve) => {
				originalImageElement.onload = resolve;
			});

			const canvasSizeDiffers = this.isCanvasSizeDiffers(originalImageElement, customCropSize, canvasSize);

			// Check if the original image has transparent pixels (needed for both paths)
			const hasTransparentPixels = await checkImageHasTransparentPixels(image.url);

			// Regular crop: call the /crop API
			const formData = new FormData();
			const { seed, style_props, ...sanitizedImage } = image;

			// Create config object
			const configObject = {
				...config,
				manual_placement: canvasSizeDiffers ? config.manual_placement : {},
				original_image: {
					...sanitizedImage,
					url: croppedImageUrl,
					offline_props: {
						...image.offline_props,
					},
				},
			};

			formData.append("config", JSON.stringify(configObject));

			const result = await this.queryService.post(
				"/crop",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.resizingAbortController.signal },
			);

			if (!result) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			} else if (result) {
				const { playgroundResults } = playgroundStore;
				const { projectsStore } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: result,
					loading: false,
					type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
						? APPS.AUTOMOTIVE
						: APPS.IMAGE_TO_IMAGE,
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
						...(hasTransparentPixels ? {} : { operation: "crop" }),
						size: {},
					} as ImageToImageConfigType,
					image_config: {
						size: {
							crop: true,
						},
					} as ImageToImageConfigType,
					mode: "single",
				};

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);
				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}

				iframeStore.sendImageEditingPostMessage({
					action: IframePostMessageActionTypes.Generate,
					feature: ImageEditingFeatureDetector.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.CROP_IMAGE],
					inputImage: image,
					resultUrls: [result],
					config: { ...config, size: { ...config.size, crop: true } },
				});

				// Increment global generation count (for engagement popup logic)
				this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
			}
		} catch (e) {
			console.error(e);

			this.imageToImageStore.showAPIError(e);
			this.rootStore.playgroundStore.removeNotLoadingImages();
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	increaseImageResolution = async (
		image: PlaygroundImage,
		resolution: number,
		config: ImageToImageConfigType = this.imageToImageStore.config,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const savedIndex = playgroundStore.playgroundResults.length;

		runInAction(() => {
			this.isResizingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		playgroundStore.handleSelectSingleImage(null);
		try {
			const scaledUrl = await new ImagesService().increaseResolution(
				image.url,
				resolution,
				config.content_moderation,
			);
			if (!scaledUrl) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			}
			if (scaledUrl) {
				const { playgroundResults } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: scaledUrl,
					loading: true,
					type: APPS.IMAGE_TO_IMAGE,
					mode: "single",
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
						size: {
							increase_resolution: this.imageToImageStore.config.size?.increase_resolution,
						},
					} as ImageToImageConfigType,
					image_config: {
						size: {
							increase_resolution: this.imageToImageStore.config.size?.increase_resolution,
						},
					} as ImageToImageConfigType,
				};

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}

				iframeStore.sendImageEditingPostMessage({
					action: IframePostMessageActionTypes.Generate,
					feature:
						ImageEditingFeatureDetector.getFeatureDisplayNames()[
							IMAGE_EDITING_FEATURES.INCREASE_RESOLUTION
						],
					inputImage: image,
					resultUrls: [scaledUrl],
					config: {
						...config,
						size: {
							...config.size,
							increase_resolution: {
								selected: true,
								value: config.size?.increase_resolution?.value || [String(resolution)],
							},
						},
					},
				});

				// Increment global generation count (for engagement popup logic)
				this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
			}
		} catch (e: any) {
			if (e?.message.includes("422")) {
				runInAction(() => {
					this.imageToImageStore.thumbnailNavigationSlideToImage = image;
				});
			}
			this.imageToImageStore.showAPIError(e);
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_: PlaygroundResult, index: number) => index !== savedIndex,
				);
				throw new Error(`Error Increasing resolution: ${e.message || e.toString()}`);
			});
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	toggleManualPlacementConfig = (status: boolean) => {
		runInAction(() => {
			this.isManualPlacementConfigEnabled = status;
		});
	};
}
